import React, { useState, useRef } from "react";
import { HomeOutlined, SearchOutlined } from "@ant-design/icons";
import { Breadcrumb, Table, Input, Button, Space } from "antd";
import type { InputRef } from 'antd';
import type { ColumnType, ColumnsType } from 'antd/es/table';
import type { FilterConfirmProps } from 'antd/es/table/interface';
import "./data_wh_transfer_out.css";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";

interface DataType {
    key: React.Key;
    transRequestNo: string;
    requestDate: string;
    requestBy: string;
    warehouse: string;
    fromWarehouse: string;
    orderStatus: string;
}

type DataIndex = keyof DataType;
const fetcher = (url: string) => axios.get(url).then(res => res.data);

const DataTransferWhOut: React.FC = () => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const navigate = useNavigate();
    const { userData } = useAuth();
    const [fetching, setFetching] = useState(false);

    const { data, error, isLoading } = useSWR<DataType[]>(userData?.warehouse_code ? `${process.env.REACT_APP_BACKEND_URL}/api/item-wh-transfer-receipts?warehouse_code=${userData.warehouse_code}` : null, fetcher);
    const handleRowDoubleClick = async (record: DataType) => {
        setFetching(true);
        try {
            const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/wh-transfer-request/${record.transRequestNo}`);
            navigate('/dashboard/item_wh_transfer_request', { state: { requestData: res.data, fromReceipt: true } });
        } catch (err) {
            console.error("Failed to fetch details:", err);
        } finally {
            setFetching(false);
        }
    };
    const getStatusText = (status: string): string => {
        switch (status) {
            case '1':
                return 'Draft';
            case '2':
                return 'Accept';
            case '3':
                return 'Ready';
            case '4':
                return 'Reject';
            case '5':
                return 'Closed';
            case '6':
                return 'intransit';
            default:
                return 'Unknown';
        }
    };


    const handleSearch = (
        selectedKeys: string[],
        confirm: (param?: FilterConfirmProps) => void,
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };


    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };


    const getColumnSearchProps = (dataIndex: DataIndex): ColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) => text,
    });


    const columns: ColumnsType<DataType> = [
        {
            title: 'Transfer Request No',
            dataIndex: 'transRequestNo',
            key: 'transRequestNo',
            sorter: (a, b) => a.transRequestNo.localeCompare(b.transRequestNo),
            ...getColumnSearchProps('transRequestNo'),
        },
        {
            title: 'Request Date',
            dataIndex: 'requestDate',
            key: 'requestDate',
            sorter: (a, b) => new Date(a.requestDate).getTime() - new Date(b.requestDate).getTime(),
            ...getColumnSearchProps('requestDate'),
        },
        {
            title: 'Request By',
            dataIndex: 'requestBy',
            key: 'requestBy',
            sorter: (a, b) => a.requestBy.localeCompare(b.requestBy),
            ...getColumnSearchProps('requestBy'),
        },
        {
            title: 'To Warehouse',
            dataIndex: 'warehouse',
            key: 'warehouse',
            sorter: (a, b) => a.warehouse.localeCompare(b.warehouse),
            ...getColumnSearchProps('warehouse'),
        },
        {
            title: 'From Warehouse',
            dataIndex: 'fromWarehouse',
            key: 'fromWarehouse',
            sorter: (a, b) => a.fromWarehouse.localeCompare(b.fromWarehouse),
            ...getColumnSearchProps('fromWarehouse'),
        },
        {
            title: 'Status',
            dataIndex: 'orderStatus',
            key: 'orderStatus',
            sorter: (a, b) => a.orderStatus.localeCompare(b.orderStatus),
            ...getColumnSearchProps('orderStatus'),
        },
    ];

    return (

        <div id="iiok" style={transformStyle}>
            <div className="gjs-grid-row" id="i2fk">
                <div className="gjs-grid-column" id="il8s">
                </div>
            </div>
            <div className="gjs-grid-row" id="i2jh">
                <div className="gjs-grid-column" id="iueh">
                    <Table
                        columns={columns}
                        dataSource={Array.isArray(data) ? data : []}
                        loading={isLoading || fetching}
                        style={{ paddingTop: '20px' }}
                        onRow={(record) => {
                            return {
                                onDoubleClick: () => {
                                    handleRowDoubleClick(record);
                                },
                            };
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default DataTransferWhOut;